import React, { useEffect, useMemo, useRef } from "react";
import AdminPortal from "./AdminPortal";
import { X, ExternalLink, Monitor } from "lucide-react";
import { SettingsContext } from "../../contexts/SettingsContext";
import PreviewContext from "../../contexts/PreviewContext";

/**
 * Windowed modal overlay that renders a public page component directly,
 * overriding SettingsContext so unsaved changes are visible immediately.
 *
 * MainLayout reads PreviewContext to drop its h-[100dvh] constraint so the
 * page scrolls end-to-end inside the modal.
 */
export default function PagePreviewModal({ title, routePath, data, onClose, scrollToId, children }) {
  const contentRef = useRef(null);

  // Lock scroll on the admin page behind the modal
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const mockCtx = useMemo(
    () => ({
      settings: data,
      loading: false,
      error: null,
      refresh: () => Promise.resolve(data),
      setSettings: () => {},
    }),
    [data]
  );

  // Auto-scroll to the requested section id inside rendered page
  useEffect(() => {
    const id = typeof scrollToId === "string" ? scrollToId.trim() : "";
    if (!id) return;

    const t = setTimeout(() => {
      const root = contentRef.current;
      if (!root) return;
      const safeId = (typeof CSS !== "undefined" && CSS.escape) ? CSS.escape(id) : id.replace(/[^a-zA-Z0-9_-]/g, "");
      const el = root.querySelector(`#${safeId}`);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "instant", block: "start" });
      }
    }, 50);

    return () => clearTimeout(t);
  }, [scrollToId, children]);

  return (
    /* ── backdrop ── */
    <AdminPortal>
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 md:p-10"
        style={{ background: "rgba(51,51,51,0.60)", backdropFilter: "blur(3px)" }}
        onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        {/* ── dialog window ── */}
        <div
          className="relative flex w-full max-w-5xl flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-black/20"
          style={{ height: "min(90vh, 860px)" }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* ── title bar ── */}
          <div className="flex shrink-0 items-center gap-3 border-b border-white/10 bg-slate-900 px-4 py-2.5">
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={onClose}
                className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-red-500 transition hover:brightness-90"
                aria-label="Đóng"
              />
              <span className="h-3.5 w-3.5 rounded-full bg-yellow-400/60" />
              <span className="h-3.5 w-3.5 rounded-full bg-green-500/60" />
            </div>
            <Monitor size={13} className="text-white/60" />
            <span className="text-sm font-semibold text-white">{title}</span>
            <div className="ml-auto flex items-center gap-2">
              {routePath && (
                <a
                  href={routePath}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-2.5 py-1 text-xs font-semibold text-white/80 transition hover:bg-white/10"
                >
                  <ExternalLink size={11} />
                  Tab mới
                </a>
              )}
              <button
                type="button"
                onClick={onClose}
                className="flex h-7 w-7 items-center justify-center rounded-lg text-white/70 transition hover:bg-white/10 hover:text-white"
                aria-label="Đóng"
              >
                <X size={15} />
              </button>
            </div>
          </div>

          {/* ── address bar ── */}
          <div className="flex shrink-0 items-center gap-2 border-b border-white/10 bg-slate-800 px-3 py-1.5">
            <div className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md bg-white/10 px-2.5 py-1">
              <span className="shrink-0 text-[10px] text-white/60">🔒</span>
              <span className="truncate font-mono text-[11px] text-white/80">
                {(data?.siteUrl || "http://localhost:3000").replace(/\/$/, "")}{routePath || ""}
              </span>
            </div>
            <span className="shrink-0 text-[10px] text-white/70">● chưa lưu</span>
          </div>

          {/* ── page content — scrollable ── */}
          <div ref={contentRef} className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-white">
            <PreviewContext.Provider value={true}>
              <SettingsContext.Provider value={mockCtx}>
                {children}
              </SettingsContext.Provider>
            </PreviewContext.Provider>
          </div>
        </div>
      </div>
    </AdminPortal>
  );
}
